import { xaiCostUsdFromTicks } from "../../../providerEvents/grokUsage"
import type { MessageGroup, ResultGroup, ThinkingGroup, ToolGroup } from "../messageGroups"

interface GrokUpdate {
    sessionUpdate?: string
    toolCallId?: string
    title?: string
    kind?: string
    status?: string
    stop_reason?: string
    content?: unknown
    rawInput?: unknown
    rawOutput?: unknown
    usage?: unknown
}

export function groupGrokProviderEvents(messages: unknown[]): MessageGroup[] {
    const groups: MessageGroup[] = []
    const tools = new Map<string, ToolGroup>()

    for (let index = 0; index < messages.length; index++) {
        const raw = recordValue(messages[index])
        if (!raw) continue
        if (raw.type === "session.started") {
            groups.push({
                type: "system",
                subtype: "init",
                metadata: raw,
                messageIndex: index,
            })
            continue
        }
        const params = recordValue(raw.params)
        const update = recordValue(params?.update) as GrokUpdate | null
        if (!update) continue

        switch (update.sessionUpdate) {
            case "agent_message_chunk": {
                const text = contentText(update.content)
                if (text) appendText(groups, text, index)
                break
            }
            case "agent_thought_chunk": {
                const text = contentText(update.content)
                if (text) appendThinking(groups, text, index)
                break
            }
            case "tool_call":
            case "tool_call_update": {
                const id = update.toolCallId
                if (!id) break
                const existing = tools.get(id)
                const result = contentText(update.content) ?? stringifyResult(update.rawOutput)
                const isError = update.status === "failed" || update.status === "error"
                if (existing) {
                    if (update.title) existing.toolName = update.title
                    if (update.rawInput !== undefined) existing.input = update.rawInput
                    if (result !== undefined) existing.result = result
                    if (typeof update.status === "string") existing.isError = isError
                    existing.messageIndices[1] = index
                    break
                }
                const group: ToolGroup = {
                    type: "tool",
                    toolUseId: id,
                    toolName: update.title ?? update.kind ?? "Tool",
                    input: update.rawInput ?? {},
                    ...(result !== undefined ? { result } : {}),
                    isError,
                    messageIndices: [index, update.sessionUpdate === "tool_call_update" ? index : undefined],
                }
                tools.set(id, group)
                groups.push(group)
                break
            }
            case "turn_completed":
                groups.push(grokResultGroup(update, index))
                break
            default:
                if (update.sessionUpdate?.includes("error")) {
                    groups.push({
                        type: "result",
                        subtype: "error_during_execution",
                        durationMs: 0,
                        totalCostUsd: 0,
                        usage: { inputTokens: 0, outputTokens: 0 },
                        isError: true,
                        errors: [contentText(update.content) ?? update.title ?? "Grok turn failed"],
                        messageIndex: index,
                    })
                }
        }
    }

    return groups
}

function appendText(groups: MessageGroup[], text: string, messageIndex: number): void {
    const previous = groups.at(-1)
    if (previous?.type === "text") {
        previous.text += text
        previous.endMessageIndex = messageIndex
        return
    }
    groups.push({ type: "text", text, messageIndex })
}

function appendThinking(groups: MessageGroup[], text: string, messageIndex: number): void {
    const previous = groups.at(-1)
    if (previous?.type === "thinking") {
        previous.text += text
        previous.endMessageIndex = messageIndex
        return
    }
    const group: ThinkingGroup = { type: "thinking", text, messageIndex }
    groups.push(group)
}

function grokResultGroup(update: GrokUpdate, messageIndex: number): ResultGroup {
    const usage = recordValue(update.usage)
    const stopReason = stringValue(update.stop_reason)
    const isError = stopReason === "error" || stopReason === "failed"
    return {
        type: "result",
        subtype: isError ? "error_during_execution" : "success",
        durationMs: numberValue(usage?.apiDurationMs) ?? 0,
        totalCostUsd: xaiCostUsdFromTicks(numberValue(usage?.costUsdTicks)),
        usage: {
            inputTokens: numberValue(usage?.inputTokens) ?? 0,
            outputTokens: numberValue(usage?.outputTokens) ?? 0,
            ...(numberValue(usage?.cachedReadTokens) !== undefined ? { cacheReadTokens: numberValue(usage?.cachedReadTokens) } : {}),
            ...(numberValue(usage?.cacheCreationTokens) !== undefined ? { cacheWriteTokens: numberValue(usage?.cacheCreationTokens) } : {}),
        },
        isError,
        ...(isError ? { errors: ["Grok turn failed"] } : {}),
        messageIndex,
    }
}

function contentText(value: unknown): string | undefined {
    const record = recordValue(value)
    if (record?.type === "text") return stringValue(record.text)
    if (!Array.isArray(value)) return undefined
    const parts: string[] = []
    for (const item of value) {
        const outer = recordValue(item)
        const content = recordValue(outer?.content) ?? outer
        const text = stringValue(content?.text)
        if (text) parts.push(text)
    }
    return parts.length > 0 ? parts.join("\n") : undefined
}

function stringifyResult(value: unknown): string | undefined {
    if (value === undefined || value === null) return undefined
    if (typeof value === "string") return value
    try {
        return JSON.stringify(value, null, 2)
    } catch {
        return String(value)
    }
}

function recordValue(value: unknown): Record<string, unknown> | null {
    return typeof value === "object" && value !== null && !Array.isArray(value) ? (value as Record<string, unknown>) : null
}

function stringValue(value: unknown): string | undefined {
    return typeof value === "string" ? value : undefined
}

function numberValue(value: unknown): number | undefined {
    return typeof value === "number" && Number.isFinite(value) ? value : undefined
}
